import Swiper, { A11y, Navigation, Pagination } from 'swiper';

const initializedWrappers = new WeakSet();
const mobileQuery = window.matchMedia('(max-width: 767px)');
const svgNamespace = 'http://www.w3.org/2000/svg';

const initSearchDeliverSliders = (scope = document) => {
  const wrappers = scope.matches?.('.search-deliver-swiper-wr')
    ? [scope]
    : scope.querySelectorAll('.search-deliver-swiper-wr');

  wrappers.forEach((wrapper) => {
    if (initializedWrappers.has(wrapper)) {
      return;
    }

    const slider = wrapper.querySelector('.swiper');
    const list = wrapper.querySelector('.search-deliver-sec__list');
    const items = Array.from(wrapper.querySelectorAll('.search-deliver-sec__item'));
    const connectors = wrapper.querySelector('.search-deliver-sec__connectors');
    const buttons = wrapper.querySelector('.search-deliver-sec__buttons');
    const prev = wrapper.querySelector('.swiper-button-prev');
    const next = wrapper.querySelector('.swiper-button-next');
    const pagination = wrapper.querySelector('.swiper-pagination');

    if (!slider || !list || !items.length || !connectors) {
      return;
    }

    initializedWrappers.add(wrapper);
    let layoutFrame = 0;
    let layoutKey = '';

    const resetMobileLayout = () => {
      wrapper.classList.remove('is-layout-ready');
      list.style.removeProperty('height');
      items.forEach((item) => {
        item.style.removeProperty('left');
        item.style.removeProperty('top');
        item.style.removeProperty('width');
      });
      layoutKey = '';
    };

    const layoutDesktop = () => {
      layoutFrame = 0;

      if (mobileQuery.matches || !wrapper.isConnected) {
        return;
      }

      const width = list.clientWidth;
      const styles = window.getComputedStyle(list);
      const columnGap = Number.parseFloat(styles.columnGap) || 35;
      const rowGap = Number.parseFloat(styles.rowGap) || 155;
      const cardWidth = (width - columnGap) / 2;
      const rightOffset = Math.min(174, Math.max(90, 90 + (window.innerWidth - 900) * 84 / 540));

      if (width <= 0 || cardWidth <= 0) {
        return;
      }

      items.forEach((item) => {
        if (Math.abs(item.getBoundingClientRect().width - cardWidth) > 0.5) {
          item.style.width = `${cardWidth}px`;
        }
      });

      const heights = items.map((item) => item.getBoundingClientRect().height);
      const nextLayoutKey = [width, columnGap, rowGap, rightOffset, ...heights].join(':');

      if (!wrapper.classList.contains('is-layout-ready') || nextLayoutKey !== layoutKey) {
        const columnTops = [0, rightOffset];
        const placements = items.map((item, index) => {
          const column = index % 2;
          const top = columnTops[column];
          const left = column ? cardWidth + columnGap : 0;
          const height = heights[index];
          columnTops[column] += height + rowGap;

          item.style.left = `${left}px`;
          item.style.top = `${top}px`;

          return { bottom: top + height };
        });

        const height = Math.max(...placements.map((placement) => placement.bottom));
        list.style.height = `${height}px`;
        if (!wrapper.classList.contains('is-layout-ready')) {
          wrapper.classList.add('is-layout-ready');
        }
        layoutKey = nextLayoutKey;
      }

      const sliderRect = slider.getBoundingClientRect();
      const cardRects = items.map((item) => {
        const rect = item.getBoundingClientRect();

        return {
          left: rect.left - sliderRect.left,
          right: rect.right - sliderRect.left,
          top: rect.top - sliderRect.top,
          bottom: rect.bottom - sliderRect.top,
          width: rect.width,
        };
      });

      const viewBox = `0 0 ${sliderRect.width} ${sliderRect.height}`;
      if (connectors.getAttribute('viewBox') !== viewBox) {
        connectors.setAttribute('viewBox', viewBox);
      }

      const itemStyles = window.getComputedStyle(items[0]);
      const parsedRadius = Number.parseFloat(itemStyles.borderTopLeftRadius) || 31;
      const borderRadius = Math.max(22, Math.min(31, parsedRadius || (22 + (window.innerWidth - 900) * 9 / 540)));
      const cornerOffset = borderRadius + 6;

      const pathsCount = Math.max(0, cardRects.length - 2);
      for (let k = 0; k < pathsCount; k += 1) {
        const preceding = cardRects[k];
        const source = cardRects[k + 1];
        const target = cardRects[k + 2];
        const isTargetInRightColumn = (k + 2) % 2 === 1;

        const minSourceY = source.top + cornerOffset;
        const maxSourceY = source.bottom - cornerOffset;
        let y = (preceding.bottom + target.top) / 2;

        if (maxSourceY >= minSourceY) {
          y = Math.max(minSourceY, Math.min(maxSourceY, y));
        }
        if (y > target.top - cornerOffset) {
          y = Math.max(minSourceY, target.top - cornerOffset);
        }

        const startX = isTargetInRightColumn ? source.right : source.left;
        const endX = target.left + target.width / 2;
        const path = connectors.children[k] || document.createElementNS(svgNamespace, 'path');
        const definition = `M ${startX} ${y} H ${endX} V ${target.top}`;

        if (path.getAttribute('d') !== definition) {
          path.setAttribute('d', definition);
        }
        if (!path.isConnected) {
          path.setAttribute('vector-effect', 'non-scaling-stroke');
          connectors.append(path);
        }
      }

      while (connectors.children.length > pathsCount) {
        connectors.lastElementChild.remove();
      }

    };

    const scheduleLayout = () => {
      if (!mobileQuery.matches && !layoutFrame) {
        layoutFrame = window.requestAnimationFrame(layoutDesktop);
      }
    };

    const sync = () => {
      window.cancelAnimationFrame(layoutFrame);
      layoutFrame = 0;

      if (mobileQuery.matches) {
        resetMobileLayout();
        if (slider.swiper) {
          slider.swiper.destroy(true, true);
        }
        if (buttons && buttons.parentElement !== items[items.length - 1]) {
          items[items.length - 1].append(buttons);
        }
      } else {
        if (slider.swiper) {
          slider.swiper.destroy(true, true);
        }
        if (buttons && buttons.parentElement !== items[items.length - 1]) {
          items[items.length - 1].append(buttons);
        }
        scheduleLayout();
      }
    };

    mobileQuery.addEventListener('change', sync);
    window.addEventListener('resize', scheduleLayout);
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(scheduleLayout);
      items.forEach((item) => observer.observe(item));
      observer.observe(slider);
    }
    document.fonts?.ready.then(scheduleLayout);
    window.addEventListener('load', scheduleLayout, { once: true });
    sync();
  });
};

initSearchDeliverSliders();
window.initSearchDeliverSliders = initSearchDeliverSliders;
